// Native Tracking types for the Loyalty module (Tracking tab). These mirror the
// `stores/{storeId}/loyalty/{metrics,profiles,...}` backend contract
// one-to-one — the wire is snake_case exactly as typed here, so the gateway
// passes payloads through untouched (no snake↔camel mapper needed, matching the
// Email-Marketing surfaces).

// ── Analytics / metrics ──────────────────────────────────────────────────────

export type LoyaltyMetricsRange = '7d' | '30d' | '90d'

export interface LoyaltyMetricsPoint {
  date: string
  count: number
  revenue: number
  checkouts: number
}

export interface LoyaltyTopMetric {
  name: string
  count: number
}

export interface LoyaltyTopProduct {
  name: string
  product_id: string | null
  quantity: number
  count: number
}

// One row per day: the `date` label plus a numeric count per metric name (the
// keys match `metric_names`). Typed as a loose record so the dynamic per-metric
// columns pass through untouched.
export type LoyaltyMetricSeriesPoint = Record<string, number | string>

export interface LoyaltyMetrics {
  range: LoyaltyMetricsRange
  revenue: number
  currency: string
  series: LoyaltyMetricsPoint[]
  top_metrics: LoyaltyTopMetric[]
  orders_total: number
  checkouts_total: number
  conversion_rate: number
  metric_names: string[]
  metric_series: LoyaltyMetricSeriesPoint[]
  top_products: LoyaltyTopProduct[]
  top_viewed_products: LoyaltyTopProduct[]
}

// ── Analytics: sessions ──────────────────────────────────────────────────────
// `GET stores/{storeId}/loyalty/analytics/sessions?range=` — session = distinct
// session_id, duration = max−min event datetime within the session.

export interface LoyaltySessionsPoint {
  date: string
  sessions: number
  avg_duration_seconds: number
  /** Avg 'Viewed Page' events per session that day (0 when no sessions). */
  pages_per_session?: number
  /** % of that day's sessions with <= 1 'Viewed Page' event. */
  bounce_rate?: number
}

export interface LoyaltySessionsAnalytics {
  sessions_total: number
  avg_duration_seconds: number
  /** Avg 'Viewed Page' events per session over the range (2 decimals). */
  pages_per_session: number
  /** % of sessions with <= 1 'Viewed Page' event (1 decimal). */
  bounce_rate: number
  series: LoyaltySessionsPoint[]
}

// ── Analytics: top earners ───────────────────────────────────────────────────
// `GET .../analytics/top-earners?range=&limit=` — top customers by Σ value of
// Placed Order events, names enriched from woocommerce_customer.

export interface LoyaltyTopEarner {
  customer_id: string
  email: string
  first_name: string
  last_name: string
  orders: number
  total_spent: number
}

// ── Analytics: search terms ──────────────────────────────────────────────────
// `GET .../analytics/search-terms?range=&limit=` — from Searched Products.

export interface LoyaltySearchTerm {
  term: string
  count: number
  last_searched: string
}

// ── Analytics: top pages ─────────────────────────────────────────────────────
// `GET .../analytics/top-pages?range=&limit=` — the Viewed Page path
// leaderboard. Paths are trailing-slash-normalized server-side (except "/"), and
// a visitor is the customer_id when signed in, else the anonymous id.

export interface LoyaltyTopPage {
  path: string
  title: string
  views: number
  unique_visitors: number
  unique_sessions: number
  last_viewed: string | null
}

// ── Analytics: campaigns ─────────────────────────────────────────────────────
// `GET .../analytics/campaigns?range=&limit=` — recent SENT campaigns with
// engagement rates. Counts come from the campaign's own stats counters (unique
// recipients, deduped by the open/click ledgers), NOT from customer_events —
// so they differ from the Opened/Clicked Email rows in Top metrics, which count
// every open/click including repeats.
//
// `open_rate`/`click_rate` are percentages 0-100, or null when nothing was sent
// (render an em-dash — a campaign with 0 sends has no rate).

export interface LoyaltyCampaignPerformance {
  id: string
  name: string
  channel: 'email' | 'sms'
  sent_at: string | null
  recipients: number
  sent: number
  opens: number
  clicks: number
  open_rate: number | null
  click_rate: number | null
}

// ── Analytics: devices ───────────────────────────────────────────────────────
// `GET .../analytics/devices?range=` — category split + browser/os splits.

export interface LoyaltyDeviceCategorySlice {
  category: string
  count: number
  percent: number
}

// Browser/OS rows come off the wire as bare {name, count} (no percent — the
// backend's _named_counts helper); the card derives shares client-side.
export interface LoyaltyDeviceBrowserSlice {
  name: string
  count: number
}

export interface LoyaltyDeviceOsSlice {
  name: string
  count: number
}

export interface LoyaltyDevicesAnalytics {
  categories: LoyaltyDeviceCategorySlice[]
  browsers: LoyaltyDeviceBrowserSlice[]
  os: LoyaltyDeviceOsSlice[]
}

// ── Analytics: geo ───────────────────────────────────────────────────────────
// `GET .../analytics/geo?range=[&country=]` — without `country` the rows are
// per-country ({country_code, country, count}); with `country` they are
// per-region ({region_code, region, count}). Both carry `count`, so the two
// row shapes are unioned here and the card discriminates on which code field
// is present.

export interface LoyaltyGeoCountryRow {
  country_code: string
  country: string
  count: number
}

export interface LoyaltyGeoRegionRow {
  region_code: string
  region: string
  count: number
}

export type LoyaltyGeoRow = LoyaltyGeoCountryRow | LoyaltyGeoRegionRow

// ── Analytics: per-customer ──────────────────────────────────────────────────
// `GET stores/{storeId}/loyalty/customers/{cid}/analytics?range=`.

export interface LoyaltyCustomerTopProduct {
  name: string
  product_id: string | null
  qty: number
  total: number
}

export interface LoyaltyCustomerCategoryCount {
  category: string
  count: number
}

export interface LoyaltyCustomerAnalytics {
  top_purchased_products: LoyaltyCustomerTopProduct[]
  sessions_total: number
  avg_session_seconds: number
  total_events: number
  first_seen: string | null
  last_seen: string | null
  categories: LoyaltyCustomerCategoryCount[]
}

// ── Event categorization (Profiles → Activity) ───────────────────────────────
// Frontend-only grouping of tracked metrics into six scannable categories, per
// the tracking-module spec §2.3. Metrics outside the map are uncategorized
// (categoryForMetric → null) and render plain.

export type LoyaltyEventCategory =
  | 'Browse'
  | 'Search'
  | 'Cart'
  | 'Checkout'
  | 'Order'
  | 'Account'
  | 'Marketing'

// Key order drives the activity dialog's pill order. "Search" sits right after
// "Browse" and owns "Searched Products" (moved out of Browse) so it renders as a
// dedicated tab and the reverse map resolves that metric unambiguously.
export const EVENT_CATEGORIES: Record<LoyaltyEventCategory, string[]> = {
  Browse: ['Viewed Product', 'Viewed Section'],
  Search: ['Searched Products'],
  Cart: ['Added to Cart'],
  Checkout: ['Started Checkout', 'Placed Order', 'Ordered Product'],
  Order: ['Fulfilled Order', 'Cancelled Order', 'Refunded Order'],
  Account: ['Registered', 'Logged In'],
  Marketing: ['Subscribed to Newsletter'],
}

const METRIC_TO_CATEGORY: Record<string, LoyaltyEventCategory> = Object.entries(
  EVENT_CATEGORIES,
).reduce(
  (acc, [category, metrics]) => {
    for (const metric of metrics) {
      acc[metric] = category as LoyaltyEventCategory
    }
    return acc
  },
  {} as Record<string, LoyaltyEventCategory>,
)

export const categoryForMetric = (
  metric: string,
): LoyaltyEventCategory | null => METRIC_TO_CATEGORY[metric] ?? null

// ── Profiles list ────────────────────────────────────────────────────────────

export type LoyaltyProfilesFilter = 'identified' | 'anonymous' | 'all'

export interface LoyaltyProfile {
  customer_id: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  subscribed: boolean | null
  is_loyalty_member: boolean
  // Anonymous-visitor fields (present when is_anonymous). Identified rows carry
  // is_anonymous:false, anonymous_id:null and null counters.
  is_anonymous?: boolean
  anonymous_id?: string | null
  event_count?: number | null
  last_seen?: string | null
}

export interface LoyaltyProfilesParams {
  search?: string
  limit?: number
  cursor?: string
  exclude_unsubscribed?: boolean
  filter?: LoyaltyProfilesFilter
}

export interface LoyaltyProfilesResponse {
  profiles: LoyaltyProfile[]
  total_items: number
  next_cursor: string | null
}

export interface LoyaltyConsentInput {
  email: string
  subscribed: boolean
}

export interface LoyaltyConsentResult {
  email: string
  subscribed: boolean
}

// ── Per-customer activity feed (Activity dialog) ─────────────────────────────

export interface LoyaltyCustomerActivityEvent {
  id: string
  metric: string
  datetime: string
  value?: number | null
  properties?: Record<string, unknown>
  source?: string
}

export interface LoyaltyCustomerActivityParams {
  limit?: number
  before?: string
  // Optional server-side category filter (a `LoyaltyEventCategory` name). The
  // backend resolves it to that category's metric names and returns only those
  // events, so a category tab lists ALL its events across pages.
  category?: string
}

export interface LoyaltyCustomerActivityResponse {
  events: LoyaltyCustomerActivityEvent[]
  next_cursor: string | null
}
